import React from 'react';
import _ from 'lodash';
import { useMutation, useParameterizedQuery } from 'react-fetching-library';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  IconButton,
  Card,
  CardHeader,
  Stack,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { DataGridPro, GridRow } from '@mui/x-data-grid-pro';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { employeeSunActiveRecordColumns } from '../../../../../services/column-definitions';
import { submitEmployeeSunActiveRecord, deleteEmployeeSunActiveRecord } from '../../../../../services/request';
import { useEmployees } from '../../EmployessContext';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';


export default function SunActivePoints() {
  const { rowData, setRowData } = useEmployees();
  const { mutate, loading } = useMutation(submitEmployeeSunActiveRecord);
  const { mutate: deleteMutate } = useMutation(deleteEmployeeSunActiveRecord);

  const [event, setEvent] = React.useState(null); // 'ADD' | 'EXCHANGE'
  const [modalOpen, setModalOpen] = React.useState(false);
  const [activeData, setActiveData] = React.useState({});
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [expandedRows, setExpandedRows] = React.useState({});

  const openAdd = () => {
    setEvent('ADD');
    setActiveData({});
    setModalOpen(true);
  };

  const openExchange = () => {
    setEvent('EXCHANGE');
    setActiveData({});
    setModalOpen(true);
  };

  const handleEdit = row => {
    setEvent(row.value >= 0 ? 'ADD' : 'EXCHANGE');
    setActiveData(row);
    setModalOpen(true);
  };

  const handleDelete = async id => {
    const { error } = await deleteMutate(id);
    if (error) {
      return toast.error('Nie udało się usunąć');
    }

    setRowData(prev => {
      const points = prev.points.filter(p => p.id !== id);

      return {
        ...prev,
        points,
        points_sum: points.reduce((s, p) => s + p.value, 0)
      };
    });

    toast.success('Rekord usunięty');
    setConfirmDelete(false);
  };

  const handleSubmit = async values => {
    const id = activeData.id || null;
    const { payload, error } = await mutate(id, values);
    if (error) {
      return toast.error('Błąd zapisu');
    }

    setRowData(prev => {
      const points = id
        ? prev.points.map(p => (p.id === id ? payload : p))
        : [payload, ...prev.points];

      return {
        ...prev,
        points,
        points_sum: points.reduce((s, p) => s + p.value, 0)
      };
    });

    toast.success(id ? 'Zaktualizowano' : 'Dodano');
    setModalOpen(false);
  };

  const AccordionRow = props => {
    const expanded = expandedRows[props.row.id];

    return (
      <div>
        <GridRow {...props} onClick={() => setExpandedRows(p => ({ ...p, [props.row.id]: !expanded }))} />
        {expanded && (
          <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2">Opis:</Typography>
            <Typography>{props.row.description}</Typography>
          </Box>
        )}
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader
          action={
            loading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <IconButton color="primary" onClick={openAdd}>
                  <AddCircleIcon /> Dodaj
                </IconButton>
                <IconButton color="error" onClick={openExchange}>
                  <RemoveCircleIcon /> Wymień
                </IconButton>
              </>
            )
          }
        />

        <Typography sx={{ m: 2 }}>
          Aktualny stan punktów: <b>{rowData.points_sum}</b>
        </Typography>

        <DataGridPro
          autoHeight
          rows={rowData.points || []}
          columns={employeeSunActiveRecordColumns(handleEdit, r => {
            setActiveData(r);
            setConfirmDelete(true);
          })}
          components={{ Row: AccordionRow }}
        />
      </Card>

      <ConfirmDialog open={confirmDelete} setOpen={setConfirmDelete} onConfirm={() => handleDelete(activeData.id)}>
        Czy na pewno usunąć rekord?
      </ConfirmDialog>

      <FormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        handleSubmit={handleSubmit}
        data={activeData}
        event={event}
      />
    </>
  );
}


const FormModal = ({ open, onClose, handleSubmit, data, event }) => {
  const { rowData, choices } = useEmployees();

  const categoryType =
    event === 'ADD'
      ? 1
      : event === 'EXCHANGE'
        ? 2
        : null;

  const categories = React.useMemo(() => {
    if (!categoryType) {
      return [];
    }

    return (choices.sun_active_dynamic_categories || []).filter(
      c => c.type === categoryType
    );
  }, [choices, categoryType]);

  const formik = useFormik({
    initialValues: {
      employee: rowData.id,
      application: data.application || null,
      camp: data.camp || null,
      dynamic_category: data.dynamic_category || '',
      value: data.value || 0,
      description: data.description || ''
    },
    validate: values => {
      const errors = {};
      const newSum =
        rowData.points_sum - (data.value || 0) + Number(values.value);

      if (newSum < 0) {
        errors.value = 'Suma punktów nie może być ujemna';
      }

      if (event === 'ADD' && values.value <= 0) {
        errors.value = 'Wartość musi być > 0';
      }

      if (event === 'EXCHANGE' && values.value >= 0) {
        errors.value = 'Wartość musi być < 0';
      }

      return errors;
    },
    onSubmit: handleSubmit
  });

  React.useEffect(() => {
    formik.setValues({
      employee: rowData.id,
      application: data.application || null,
      camp: data.camp || null,
      dynamic_category: data.dynamic_category || '',
      value: data.value || 0,
      description: data.description || ''
    });
    formik.setErrors({});
  }, [data]);

  const selectedCategory = categories.find(
    c => c.id === formik.values.dynamic_category
  );

  const isValueLocked = selectedCategory?.value !== null;

  const handleCategoryChange = e => {
    const categoryId = e.target.value;
    const selected = categories.find(c => c.id === categoryId);

    if (!selected) {
      return;
    }

    formik.setFieldValue('dynamic_category', selected.id);

    if (selected.value !== null) {
      const finalValue =
        event === 'EXCHANGE'
          ? -Math.abs(selected.value)
          : Math.abs(selected.value);

      formik.setFieldValue('value', finalValue);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        {data.id
          ? 'Edytuj punkty'
          : event === 'ADD'
            ? 'Dodaj punkty'
            : 'Wymień punkty'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={2}>
          <FormControl>
            <InputLabel>Aplikacja</InputLabel>
            <Select
              name="application"
              labelId = "application_field_label"
              id="application_field"
              value={formik.values.application}
              label="Aplikacja"
              onChange={formik.handleChange}
              fullWidth
            >
              {!_.isEmpty(rowData.applications)
                ? _.map(rowData.applications, obj => (
                  <MenuItem value={obj.id} key={obj.id} style={{ whiteSpace: 'normal' }}>
                    Aplikacja nr {obj.id} - {obj.function_type_display}
                  </MenuItem>
                ))
                : []}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Obóz</InputLabel>
            <Select
              name="camp"
              labelId = "camp_field_label"
              id="camp_field"
              value={formik.values.camp}
              onChange={formik.handleChange}
              fullWidth
            >
              {!_.isEmpty(rowData?.choices?.camps)
                ? _.map(rowData.choices.camps, obj => (
                  <MenuItem value={obj.value} key={obj.value} style={{ whiteSpace: 'normal' }}>
                    {obj.label}
                  </MenuItem>
                ))
                : <MenuItem disabled>Brak dostępnych obozów</MenuItem>}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Kategoria</InputLabel>
            <Select
              name="dynamic_category"
              value={formik.values.dynamic_category}
              onChange={handleCategoryChange}
            >
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            name="value"
            label="Wartość"
            type="number"
            fullWidth
            value={formik.values.value}
            onChange={formik.handleChange}
            disabled={isValueLocked}
            error={Boolean(formik.errors.value)}
            helperText={
              isValueLocked
                ? 'Wartość może zostać ustalona automatycznie przez kategorię'
                : formik.errors.value
            }
          />

          <TextField
            name="description"
            label="Opis"
            multiline
            rows={4}
            fullWidth
            value={formik.values.description}
            onChange={formik.handleChange}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

